import React, { useRef, useState } from 'react';
import { CheckIcon, MinusIcon, PlusIcon, PackageIcon, Trash2Icon, AlertCircleIcon } from 'lucide-react';
import { inventoryKey, type InventoryItem } from '../../data/inventory';
import { itemImages } from '../../data/itemImages';
import type { Lang } from '../../types/funnel';
interface InventoryItemCardProps {
  item: InventoryItem;
  lang: Lang;
  inventory: Record<string, number>;
  suggested: Record<string, number>;
  onSet: (key: string, n: number) => void;
  onRemove: (keys: string[]) => void;
  labels: {
    suggested: string;
    dec: string;
    inc: string;
    remove: string;
    review: string;
  };
}

/**
 * Visual, selectable inventory card. Starts at zero. Tapping the image adds ONE unit of the active variant
 * (guarded against accidental double taps). Each variant keeps its own quantity.
 */
export function InventoryItemCard({
  item,
  lang,
  inventory,
  suggested,
  onSet,
  onRemove,
  labels
}: InventoryItemCardProps) {
  const variants = item.variants ?? [];
  const keys = variants.length ? variants.map(v => inventoryKey(item.id, v.id)) : [item.id];
  const firstSelected = keys.find(k => (inventory[k] ?? 0) > 0);
  const [activeKey, setActiveKey] = useState<string>(firstSelected ?? keys[0]);
  const lastTap = useRef(0);
  const qty = inventory[activeKey] ?? 0;
  const total = keys.reduce((s, k) => s + (inventory[k] ?? 0), 0);
  const on = total > 0;
  const activeVariant = variants.find(v => inventoryKey(item.id, v.id) === activeKey);
  const sugg = keys.reduce((s, k) => s + (suggested[k] ?? 0), 0);
  const src = itemImages[item.id];
  const name = item.label[lang];
  const tapAdd = () => {
    const now = Date.now();
    if (now - lastTap.current < 350) return; // double-tap guard
    lastTap.current = now;
    onSet(activeKey, qty + 1);
  };
  return <div className={`relative flex flex-col overflow-hidden rounded-xl border-2 bg-white transition-[border-color,box-shadow] duration-150 ease-out ${on ? 'border-lime-dark ring-2 ring-lime' : 'border-ink-200 hover:border-ink-400'}`} data-id="element-513" data-mp="3nrp8t4zqab50">
      <button type="button" aria-label={`${labels.inc}: ${name}${activeVariant ? ` — ${activeVariant.label[lang]}` : ''}`} onClick={tapAdd} className="relative block w-full bg-[#F7F6F2] transition-transform duration-150 ease-out active:scale-[0.98]" data-id="element-514" data-mp="3kr5imi95jhud">
        {src ? <img src={src} alt="" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-contain p-2" data-id="element-515" data-mp="7iizpm6tra4e" /> : <span className="flex aspect-[4/3] w-full items-center justify-center text-ink-300" data-id="element-516" data-mp="2io9lkuvioke">
            <PackageIcon className="h-10 w-10" strokeWidth={1.5} data-id="element-517" data-mp="h8vl1gmgr7bl" />
          </span>}
        {on && <span className="absolute right-2 top-2 flex h-7 min-w-[28px] items-center justify-center gap-1 rounded-full bg-lime px-2 text-sm font-bold text-ink" aria-hidden="true" data-id="element-518" data-mp="catyumtqqrvw">
            <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} data-id="element-519" data-mp="m8hv5h6zw7g6" />
            {total}
          </span>}
        {!on && sugg > 0 && <span className="absolute left-2 top-2 rounded-md bg-white/95 px-1.5 py-0.5 text-[11px] font-semibold text-lime-dark" data-id="element-520" data-mp="14wsand92vf0z">
            {labels.suggested} · {sugg}
          </span>}
      </button>

      <div className="flex flex-1 flex-col gap-2 p-2.5" data-id="element-521" data-mp="vxf16kkyeqg9">
        <span className="font-display text-[15px] font-extrabold leading-tight text-magenta" data-id="element-522" data-mp="10tckmzrikcx9">{name}</span>

        {variants.length > 0 && <div role="radiogroup" aria-label={name} className="flex flex-wrap gap-1" data-id="element-523" data-mp="m3og7po1cy5b">
            {variants.map(v => {
          const k = inventoryKey(item.id, v.id);
          const vq = inventory[k] ?? 0;
          const sel = k === activeKey;
          return <button key={v.id} type="button" role="radio" aria-checked={sel} onClick={() => setActiveKey(k)} className={`inline-flex min-h-[30px] items-center gap-1 rounded-md border px-2 text-[12px] font-semibold leading-tight transition-[background-color,border-color] duration-150 ease-out ${sel ? 'border-ink bg-ink text-white' : vq > 0 ? 'border-lime-dark bg-lime-soft text-ink' : 'border-ink-200 bg-white text-ink-700 hover:border-ink-400'}`} data-id="element-524" data-mp="h6od9nmhgbif">
                  {v.label[lang]}
                  {vq > 0 && <span className={`rounded px-1 text-[11px] ${sel ? 'bg-lime text-ink' : 'bg-ink text-white'}`} data-id="element-525" data-mp="rtpqadn2wdfa">{vq}</span>}
                </button>;
        })}
          </div>}
        {activeVariant?.review && <p className="flex items-start gap-1 text-[11px] leading-snug text-magenta-dark" data-id="element-526" data-mp="iu2rlzlqhlpx">
            <AlertCircleIcon className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" data-id="element-527" data-mp="duyh95n2phy0" />
            {labels.review}
          </p>}
        {item.note && <p className="text-[11px] leading-snug text-ink-500" data-id="element-528" data-mp="286mzbgolq0mm">{item.note[lang]}</p>}

        <div className="mt-auto flex items-center justify-between gap-1 pt-1" data-id="element-529" data-mp="2fqp5bi2cr8g3">
          <button type="button" onClick={() => onSet(activeKey, Math.max(0, qty - 1))} disabled={qty === 0} aria-label={`${labels.dec}: ${name}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-ink-200 transition-colors duration-150 ease-out hover:bg-ink-100 disabled:opacity-30" data-id="element-530" data-mp="2fol3d5o0kjig">
            <MinusIcon className="h-4 w-4" data-id="element-531" data-mp="1wto0pycpnaco" />
          </button>
          <span className="min-w-[2ch] text-center font-display text-lg font-black tabular-nums text-ink" aria-live="polite" data-id="element-532" data-mp="21rphgcmnyxkz">
            {qty}
          </span>
          <button type="button" onClick={() => onSet(activeKey, qty + 1)} aria-label={`${labels.inc}: ${name}`} className="flex h-9 w-9 items-center justify-center rounded-lg bg-ink text-white transition-colors duration-150 ease-out hover:bg-ink-700" data-id="element-533" data-mp="2arcmr9iiknjb">
            <PlusIcon className="h-4 w-4" data-id="element-534" data-mp="1n1hr8n3tqo1t" />
          </button>
          {on && <button type="button" onClick={() => onRemove(keys)} aria-label={`${labels.remove}: ${name}`} className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-500 transition-colors duration-150 ease-out hover:bg-ink-100 hover:text-ink" data-id="element-535" data-mp="1i2n8fl5uwzhp">
              <Trash2Icon className="h-4 w-4" data-id="element-536" data-mp="13cz56ee47qrc" />
            </button>}
        </div>
      </div>
    </div>;
}